// Components
import { Badge, Button, Group, Paper, Stack, Text } from "@mantine/core";
import {
  IconAlertTriangle,
  IconClipboardCheck,
  IconUser,
} from "@tabler/icons-react";

// Types
import type { Task } from "@medplum/fhirtypes";

// Utils
import {
  isUnreadNotificationTask,
  getNotificationReadStatus,
  hasNotificationTaskCode,
} from "@benchmark/core/constants";

// Styles
import styles from "./ClinicalNotificationItem.module.css";

/**
 * Props for {@link ClinicalNotificationItem}.
 * @agentic connections
 */
export type ClinicalNotificationItemProps = {
  task: Task;
  onOpenPatient: (patientId: string) => void;
  onMarkRead: (task: Task) => void;
  markReadPending?: boolean;
};

const patientIdFromTask = (task: Task): string | undefined => {
  const ref = task.for?.reference;
  return ref?.startsWith("Patient/") ? ref.slice("Patient/".length) : undefined;
};

const formatAuthored = (authoredOn?: string): string => {
  if (!authoredOn) return "";
  const date = new Date(authoredOn);
  return Number.isNaN(date.getTime()) ? "" : date.toLocaleString();
};

/**
 * A single clinical notification row: risk context, patient link, and mark-read.
 * Urgency is conveyed by an icon + text label, never color alone (accessibility).
 * @agentic connections
 */
export const ClinicalNotificationItem = ({
  task,
  onOpenPatient,
  onMarkRead,
  markReadPending = false,
}: ClinicalNotificationItemProps) => {
  const unread = isUnreadNotificationTask(task);
  const patientId = patientIdFromTask(task);
  const isUrgent = task.priority === "urgent" || task.priority === "stat";
  const isServiceApproval = hasNotificationTaskCode(task, "service-approval");

  return (
    <Paper
      withBorder
      p="sm"
      radius="md"
      className={styles.item}
      data-unread={unread || undefined}
      data-testid="clinical-notification-item"
    >
      <Stack gap="xs">
        <Group justify="space-between" wrap="nowrap">
          <Group gap="xs" wrap="nowrap">
            {/* Once read, the chip keeps its label (it WAS urgent) but drops the
                red so it no longer competes with unread urgent notices. */}
            {isUrgent && (
              <Badge
                color={unread ? "red" : "gray"}
                variant={unread ? "filled" : "light"}
                size="sm"
                leftSection={<IconAlertTriangle size={12} aria-hidden />}
              >
                Urgent
              </Badge>
            )}
            {isServiceApproval && (
              <Badge
                variant="light"
                color="gray"
                size="sm"
                leftSection={<IconClipboardCheck size={12} aria-hidden />}
              >
                Service approval
              </Badge>
            )}
            {unread && (
              <Badge
                variant="outline"
                size="sm"
                styles={{
                  root: {
                    borderColor: "rgba(255,255,255,0.4)",
                    color: "rgba(255,255,255,0.9)",
                  },
                }}
              >
                Unread
              </Badge>
            )}
          </Group>
          <Text size="xs" className={styles.dimmedText}>
            {formatAuthored(task.authoredOn)}
          </Text>
        </Group>

        <Text size="sm" fw={500}>
          {task.description ?? "Clinical notification"}
        </Text>
        {task.for?.display && (
          <Text size="xs" className={styles.dimmedText}>
            {task.for.display}
          </Text>
        )}

        <Group gap="xs" justify="flex-end">
          {patientId && (
            <Button
              size="xs"
              variant="outline"
              leftSection={<IconUser size={14} />}
              onClick={() => onOpenPatient(patientId)}
              styles={{
                root: {
                  borderColor: "rgba(255,255,255,0.4)",
                  color: "rgba(255,255,255,0.9)",
                },
              }}
            >
              View patient
            </Button>
          )}
          {getNotificationReadStatus(task) !== "read" && (
            <Button
              size="xs"
              variant="subtle"
              loading={markReadPending}
              onClick={() => onMarkRead(task)}
              className={styles.markReadButton}
              styles={{ root: { color: "rgba(255,255,255,0.75)" } }}
            >
              Mark read
            </Button>
          )}
        </Group>
      </Stack>
    </Paper>
  );
};
